import React, { useEffect, useState, useCallback } from "react";
import { api } from "../lib/api";
import LaunchCard from "../components/LaunchCard";
import Loading from "../components/Loading";
import Countdown from "../components/Countdown";
import { PollListItem } from "../components/PollWidget";
import { useAuth } from "../context/AuthContext";
import AdminContentForm, { AdminNewButton, AdminItemWrap, useAdminSection } from "../components/AdminContentForm";
import { Search, Rocket, TrendingUp } from "lucide-react";
import { Link } from "react-router-dom";

export default function LaunchesPage() {
  const { user } = useAuth();
  const isAdmin = !!user?.is_admin;
  const [tab, setTab] = useState("upcoming");
  const [items, setItems] = useState([]);
  const [polls, setPolls] = useState([]);
  const [pollState, setPollState] = useState("open"); // open | resolved | all
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [nextLaunch, setNextLaunch] = useState(null);
  const LIMIT = 10;

  useEffect(() => {
    api.get("/launches/upcoming", { params: { limit: 1 } })
      .then(r => setNextLaunch((r.data.results || [])[0]))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (tab === "polls") {
      setLoading(true);
      api.get("/polls", { params: { state: pollState } })
        .then(r => setPolls(r.data.results || []))
        .catch(() => setPolls([]))
        .finally(() => setLoading(false));
      return;
    }
    setLoading(true);
    const url = tab === "upcoming" ? "/launches/upcoming" : "/launches/previous";
    const params = { limit: LIMIT, offset };
    if (q) params.search = q;
    api.get(url, { params })
      .then(r => { setItems(r.data.results || []); setTotal(r.data.count || 0); })
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  }, [tab, offset, q, pollState]);

  const reload = useCallback(() => {
    const url = tab === "upcoming" ? "/launches/upcoming" : "/launches/previous";
    const params = { limit: LIMIT, offset };
    if (q) params.search = q;
    api.get(url, { params }).then(r => { setItems(r.data.results || []); setTotal(r.data.count || 0); });
  }, [tab, offset, q]);
  const admin = useAdminSection("launch", reload);

  return (
    <div data-testid="page-launches">
      {/* HERO — Next launch */}
      {nextLaunch && (
        <Link to={`/launches/${nextLaunch.id}`} data-testid="next-launch-hero"
          className="block sat-surface sat-brackets mb-8 relative overflow-hidden group">
          <div className="grid grid-cols-1 md:grid-cols-[1fr_1.2fr]">
            <div className="p-8 md:p-10 flex flex-col justify-between gap-6">
              <div>
                <div className="flex items-center gap-3 mb-4">
                  <Rocket size={16} className="sat-cyan" />
                  <span className="sat-label sat-cyan">NEXT DEPARTURE // T-MINUS</span>
                </div>
                <h2 className="font-display font-bold text-3xl md:text-4xl leading-tight mb-3 group-hover:text-[color:var(--sat-cyan)] transition-colors">
                  {nextLaunch.name}
                </h2>
                <p className="text-sm text-[color:var(--sat-text-dim)] font-mono">
                  {nextLaunch.rocket?.configuration?.full_name || nextLaunch.rocket?.configuration?.name || "—"}
                </p>
                <p className="text-sm text-[color:var(--sat-text-dim)] mt-1">
                  {nextLaunch.pad?.location?.name || "TBD"}
                </p>
              </div>
              <Countdown iso={nextLaunch.net} size="lg" testid="hero-countdown" />
            </div>
            <div className="h-56 md:h-auto bg-black relative overflow-hidden">
              {nextLaunch.image ? (
                <img src={nextLaunch.image} alt={nextLaunch.name} className="w-full h-full object-cover opacity-60 group-hover:opacity-80 transition-opacity" />
              ) : <div className="w-full h-full" />}
              <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/40 to-black" />
            </div>
          </div>
        </Link>
      )}

      {/* Header */}
      <div className="mb-6 border-b border-[color:var(--sat-border)] pb-4 flex items-end justify-between flex-wrap gap-4">
        <div>
          <div className="sat-label sat-cyan">CHANNEL // 002</div>
          <h1 className="font-display font-bold text-3xl md:text-4xl mt-1">FLIGHT MANIFEST</h1>
          {isAdmin && <div className="mt-2"><AdminNewButton section="launch" label="Launch" onClick={admin.openNew} /></div>}
        </div>
        <div className="flex gap-2">
          <button data-testid="launches-tab-upcoming" onClick={() => { setTab("upcoming"); setOffset(0); }}
            className={`sat-btn ${tab === "upcoming" ? "sat-btn-solid" : ""}`}>▶ UPCOMING</button>
          <button data-testid="launches-tab-past" onClick={() => { setTab("previous"); setOffset(0); }}
            className={`sat-btn ${tab === "previous" ? "sat-btn-solid" : ""}`}>◀ PAST</button>
          <button data-testid="launches-tab-polls" onClick={() => { setTab("polls"); }}
            className={`sat-btn ${tab === "polls" ? "sat-btn-solid" : ""}`}>
            <TrendingUp size={12} /> POLLS
          </button>
        </div>
      </div>

      {tab === "polls" ? (
        <>
          <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
            <div className="sat-label">
              PREDICTION MARKET · <span className="sat-cyan">{polls.length}</span> POLL{polls.length === 1 ? "" : "S"}
            </div>
            <div className="flex gap-2">
              <button onClick={() => setPollState("open")}
                data-testid="polls-filter-open"
                className={`sat-btn ${pollState === "open" ? "sat-btn-solid" : ""}`}>OPEN</button>
              <button onClick={() => setPollState("resolved")}
                data-testid="polls-filter-resolved"
                className={`sat-btn ${pollState === "resolved" ? "sat-btn-solid" : ""}`}>RESOLVED</button>
              <button onClick={() => setPollState("all")}
                data-testid="polls-filter-all"
                className={`sat-btn ${pollState === "all" ? "sat-btn-solid" : ""}`}>ALL</button>
            </div>
          </div>
          {loading ? <Loading message="TABULATING PREDICTIONS" /> : (
            polls.length === 0 ? (
              <div className="sat-surface border border-[color:var(--sat-border)] p-8 text-center" data-testid="polls-empty">
                <div className="sat-label sat-cyan mb-2">// NO POLLS</div>
                <p className="text-sm text-[color:var(--sat-text-dim)]">
                  {pollState === "resolved"
                    ? "No polls have been resolved yet — check back after upcoming launches complete."
                    : "No polls available. New polls are auto-created for upcoming launches."}
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="polls-list">
                {polls.map(p => <PollListItem key={p.id} poll={p} />)}
              </div>
            )
          )}
        </>
      ) : (
        <></>
      )}

      {(tab === "upcoming" || tab === "previous") && (
        <>
          {/* Search */}
          <div className="relative mb-6">
            <Search size={14} className="absolute left-4 top-1/2 -translate-y-1/2 text-[color:var(--sat-text-dim)]" />
            <input type="search" placeholder="SEARCH MISSIONS…" value={q}
              onChange={(e) => { setOffset(0); setQ(e.target.value); }}
              className="!pl-10" data-testid="launches-search" />
          </div>

          {loading ? <Loading message="SYNCING FLIGHT DATA" /> : (
            <>
              <div className="flex flex-col gap-3">
                {items.map(l => (
                  <AdminItemWrap key={l.id} item={l} isAdmin={isAdmin} onEdit={admin.openEdit} onDelete={admin.onDelete}>
                    <LaunchCard launch={l} />
                  </AdminItemWrap>
                ))}
              </div>
              {items.length === 0 && <div className="text-center py-16 sat-label">NO LAUNCHES ON MANIFEST</div>}
              {total > LIMIT && (
                <div className="flex justify-between items-center mt-8 pt-6 border-t border-[color:var(--sat-border)]">
                  <button className="sat-btn" disabled={offset === 0} onClick={() => setOffset(Math.max(0, offset - LIMIT))} data-testid="launches-prev">← PREV</button>
                  <span className="sat-label">{offset + 1}–{Math.min(offset + LIMIT, total)} OF {total}</span>
                  <button className="sat-btn" disabled={offset + LIMIT >= total} onClick={() => setOffset(offset + LIMIT)} data-testid="launches-next">NEXT →</button>
                </div>
              )}
            </>
          )}
        </>
      )}

      {admin.formOpen && (
        <AdminContentForm
          section="launch"
          initial={admin.editing}
          initialId={admin.editing?.admin_id}
          onClose={admin.closeForm}
          onSaved={admin.onSaved}
        />
      )}
    </div>
  );
}
